/**
 * Soundscape Service - iOS Safari Optimized
 * Event-driven state machine for Soundscapes playback
 * 
 * Based on radioService.js architecture for iOS compatibility.
 * The key insight: iOS Safari requires audio to start in the SAME synchronous
 * "tick" as the user gesture. Any async operation (setState, await, setTimeout)
 * between the click and audioContext.resume() will break the gesture chain.
 * 
 * BUILD 66: Created to fix P0 iOS Safari play button bug
 * BUILD 66.3: Modified to use audioService's AudioContext and equalizer
 */

import { API_BASE_URL, getBaseUrl, reencodeAudioPath } from './httpClient';
import { resumeAudioContext, isUnlocked } from './iosAudioHelper';
import { audioSessionManager } from './audioSessionManager';
import audioService from './audioService';
import { backgroundAudioHelper } from './backgroundAudioHelper';

// iOS detection
const isIOSDevice = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(ua) || 
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
         window.Capacitor !== undefined;
};

class SoundscapeService {
  constructor() {
    this.audioContext = null;
    this.isPlaying = false;
    this.isPaused = false;
    this.isIOS = isIOSDevice();
    
    // ═══════════════════════════════════════════════════════════════════════
    // STATE MACHINE
    // Canonical states: 'IDLE' | 'LOADING' | 'PLAYING' | 'PAUSED' | 'ERROR'
    // ═══════════════════════════════════════════════════════════════════════
    this.playbackState = 'IDLE';
    
    // Single-flight lock with watchdog
    this.isStarting = false;
    this.startRequestId = 0;
    this._startWatchdogTimer = null;
    
    // Current track info
    this.currentTrack = null;
    this.currentCategory = null;

    // ═══════════════════════════════════════════════════════════════════════
    // P0 — GLOBAL SOUNDSCAPE SESSION
    // Keeps playback/transition logic alive outside TherapyPlayer lifecycle
    // ═══════════════════════════════════════════════════════════════════════
    this.soundscapeSession = {
      sessionId: null,
      isActive: false,
      themeId: null,
      tracks: [],
      currentIndex: 0,
      currentTrack: null,
      volume: 0.7,
      loop: false,
      startedAt: null,
    };

    this._globalOnEndedBound = this._handleGlobalTrackEnded.bind(this);
    
    // Audio nodes
    this.masterGainNode = null;
    this.inputNode = null;
    this.soundSource = null;       // BufferSourceNode for iOS
    this.soundBuffer = null;       // Decoded AudioBuffer
    this.soundGainNode = null;     // Gain for this sound
    this.fetchController = null;   // For aborting fetches
    
    // Pause/resume tracking (iOS)
    this.iosStartTime = 0;
    this.iosPausedAt = 0;
    
    // Volume
    this.currentVolume = 0.7;
    
    // Callbacks
    this.onEndedCallback = null;
    this.loop = false;
    
    // Media session handlers (registered by React components)
    this._mediaSessionHandlers = { onNext: null, onPrev: null, onStop: null };
    
    // Event listeners
    this.stateChangeListeners = [];
    this.trackChangeListeners = [];
    
    // Non-iOS: HTMLMediaElement
    this.audioElement = null;
    this.mediaSource = null;
    
    // BUG A FIX: Spectrum analyzer nodes
    this.analyserNode = null;
    
    // BUILD 66.3: Flag to track if using shared audioService context
    this.usesSharedContext = false;
    
    // ═══════════════════════════════════════════════════════════════════════
    // V1.1.1: PRELOAD PIPELINE — fetch+decode AudioBuffer in advance
    // ═══════════════════════════════════════════════════════════════════════
    this._preloadCache = null;     // { url, buffer, trackId, readyAt }
    this._preloadController = null; // AbortController for preload fetch
    this._preloading = false;
    
    // V2: CROSSFADE ENGINE — smooth transitions between tracks
    this._crossfadeDuration = 3.5; // seconds
    this._outgoingSource = null;   // BufferSourceNode being faded out
    this._outgoingGain = null;     // GainNode being faded out
    this._outgoingCleanupTimer = null;
    this.mediaSourceGain = null;   // GainNode for audioElement (desktop only)
    
    // V1.1.1: TRANSITION METRICS — instrumentation
    this._transitionMetrics = [];    // Last N transition logs
    this._currentTrackStartCtx = 0;  // audioContext.currentTime when track started
    this._metricsListeners = [];     // Callbacks for debug overlay
    
    // V1.1.1: LOCK/UNLOCK RESUME — iOS Safari
    this._bgPosition = 0;           // Playback position stored on background
    this._needsGestureResume = false; // True if resume needs a user tap
    
    console.log(`[SoundscapeService] Initialized (isIOS=${this.isIOS})`);
  }
  
  // ========================================================================
  // INITIALIZATION
  // ========================================================================
  
  initialize() {
    if (this.audioContext) return;
    
    console.log('[SoundscapeService] Initializing...');
    
    // BUILD 66.3: Use audioService's AudioContext and connect to its inputNode
    // This allows the sound to pass through audioService's equalizer
    audioService.init(); // Ensure audioService is initialized
    
    this.audioContext = audioService.audioContext;
    this.usesSharedContext = true;
    
    console.log('[SoundscapeService] Using shared AudioContext from audioService');
    
    // Register with session manager
    audioSessionManager.registerStopCallback('soundscape', () => {
      console.log('[SoundscapeService] Stop requested by AudioSessionManager');
      this.stop();
    });
    
    audioSessionManager.registerResumeCallback('soundscape', () => {
      return this.resumeIfNeeded();
    });
    
    // V1.1.1: Store playback position on background/lock
    audioSessionManager.registerBackgroundCallback('soundscape', () => {
      this.storeBackgroundPosition();
    });
    
    // BUILD 66.3: Use audioService's analyser nodes for spectrum visualization
    // This gives us both original (green) and processed (orange) data
    this.analyserNode = audioService.analyserProcessed; // Use processed for main display
    
    // BUILD 66.3: Connect to audioService's inputNode instead of our own routing
    // V1.1.1: Route through musicBusGain for ducking support
    // Routing: source -> soundscapeGain -> musicBusGain -> audioService.inputNode -> [equalizer] -> destination
    const graph = audioService.ensureAudioGraph();
    this.inputNode = this.audioContext.createGain();
    this.inputNode.gain.value = 1.0;
    this.inputNode.connect(graph.musicBusGain); // Connect to music bus (duckable)
    
    // Non-iOS: Create audio element once
    if (!this.isIOS) {
      this.audioElement = new Audio();
      this.audioElement.preload = 'auto';
      this.audioElement.crossOrigin = 'anonymous';
      
      // Connect to Web Audio API: audioElement → mediaSource → mediaSourceGain → inputNode
      this.mediaSource = this.audioContext.createMediaElementSource(this.audioElement);
      this.mediaSourceGain = this.audioContext.createGain();
      this.mediaSourceGain.gain.value = 1.0;
      this.mediaSource.connect(this.mediaSourceGain);
      this.mediaSourceGain.connect(this.inputNode);
      
      console.log('[SoundscapeService] Created HTMLMediaElement for non-iOS (with gain node)');
    }
    
    // AudioContext state monitoring
    this.audioContext.onstatechange = () => {
      console.log(`[SoundscapeService] AudioContext state: ${this.audioContext.state}`);
    };
    
    console.log('[SoundscapeService] AudioContext initialized with analyser');
  }
  
  // ========================================================================
  // STATE MACHINE HELPERS
  // ========================================================================
  
  setState(newState, reason = '') {
    const oldState = this.playbackState;
    this.playbackState = newState;
    console.log(`[SoundscapeService][State] ${oldState} → ${newState}${reason ? ' (' + reason + ')' : ''}`);
  }
  
  _clearWatchdog() {
    if (this._startWatchdogTimer) {
      clearTimeout(this._startWatchdogTimer);
      this._startWatchdogTimer = null;
    }
  }
  
  _forceErrorState(reason, reqId) {
    console.log(`[SoundscapeService] ❌ _forceErrorState reason=${reason} reqId=${reqId}`);
    this._clearWatchdog();
    this.isStarting = false;
    this.isPlaying = false;
    this.isPaused = false;
    this.playbackState = 'ERROR';
    audioSessionManager.releaseSession('soundscape');
    this.notifyStateChange('error', reqId, reason);
  }
  
  // ========================================================================
  // EVENT SYSTEM
  // ========================================================================
  
  /**
   * Subscribe to playback state changes
   * @param {Function} callback - Called with { state, track, isPlaying, reqId, error }
   * @returns {Function} - Unsubscribe function
   */
  onStateChange(callback) {
    this.stateChangeListeners.push(callback);
    return () => {
      this.stateChangeListeners = this.stateChangeListeners.filter(cb => cb !== callback);
    };
  }
  
  /**
   * Subscribe to track changes
   * @param {Function} callback - Called with (track)
   * @returns {Function} - Unsubscribe function
   */
  onTrackChange(callback) {
    this.trackChangeListeners.push(callback);
    return () => {
      this.trackChangeListeners = this.trackChangeListeners.filter(cb => cb !== callback);
    };
  }
  
  /**
   * Notify all state change listeners
   */
  notifyStateChange(state, reqId = null, error = null) {
    const payload = {
      state,
      track: this.currentTrack,
      isPlaying: state === 'playing',
      isPaused: state === 'paused',
      isLoading: state === 'loading',
      reqId: reqId || this.startRequestId,
      error
    };
    
    console.log(`[SoundscapeService][EMIT] state='${state}' isPlaying=${payload.isPlaying} reqId=${payload.reqId}`);
    
    this.stateChangeListeners.forEach(cb => {
      try {
        cb(payload);
      } catch (e) {
        console.error('[SoundscapeService] Error in state change listener:', e);
      }
    });
  }
  
  /**
   * Notify track change listeners
   */
  notifyTrackChange() {
    this.trackChangeListeners.forEach(cb => {
      try {
        cb(this.currentTrack);
      } catch (e) {
        console.error('[SoundscapeService] Error in track change listener:', e);
      }
    });
  }
  
    // ========================================================================
// P0 — GLOBAL SOUNDSCAPE SESSION
// ========================================================================

startSoundscapeSession({
  themeId,
  tracks = [],
  startIndex = 0,
  volume = 0.7,
  loop = false,
} = {}) {
  const safeTracks = Array.isArray(tracks) ? tracks : [];
  const safeIndex = Math.max(0, Math.min(startIndex, safeTracks.length - 1));
  const firstTrack = safeTracks[safeIndex] || null;

  const sessionId = `soundscape_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  this.soundscapeSession = {
    sessionId,
    isActive: true,
    themeId,
    tracks: safeTracks,
    currentIndex: safeIndex,
    currentTrack: firstTrack,
    volume,
    loop,
    startedAt: Date.now(),
  };

  console.log('[SoundscapeSession][START]', {
    sessionId,
    themeId,
    tracksCount: safeTracks.length,
    currentIndex: safeIndex,
    currentTrack: firstTrack?.name || firstTrack?.display_name || firstTrack?.file,
  });

  return this.soundscapeSession;
}

getSoundscapeSession() {
  return { ...this.soundscapeSession };
}

hasActiveSoundscapeSession() {
  return !!this.soundscapeSession?.isActive;
}

_getNextSessionTrack() {
  const session = this.soundscapeSession;

  if (!session?.isActive || !Array.isArray(session.tracks) || session.tracks.length === 0) {
    console.warn('[SoundscapeSession][NEXT] No active session or empty tracks');
    return null;
  }

  const nextIndex = (session.currentIndex + 1) % session.tracks.length;
  const nextTrack = session.tracks[nextIndex];

  console.log('[SoundscapeSession][NEXT]', {
    fromIndex: session.currentIndex,
    toIndex: nextIndex,
    nextTrack: nextTrack?.name || nextTrack?.display_name || nextTrack?.file,
  });

  return { nextIndex, nextTrack };
}

async _handleGlobalTrackEnded() {
  const session = this.soundscapeSession;

  console.log('[SoundscapeSession][ENDED]', {
    isActive: session?.isActive,
    themeId: session?.themeId,
    currentIndex: session?.currentIndex,
    currentTrack: session?.currentTrack?.name || session?.currentTrack?.display_name || session?.currentTrack?.file,
    playbackState: this.playbackState,
  });

  if (!session?.isActive) {
    console.warn('[SoundscapeSession][ENDED] ignored — no active session');
    return;
  }

  const next = this._getNextSessionTrack();

  if (!next?.nextTrack) {
    console.warn('[SoundscapeSession][ENDED] no next track — stopping');
    this.stop({ reason: 'session_no_next_track' });
    return;
  }

  session.currentIndex = next.nextIndex;
  session.currentTrack = next.nextTrack;

  try {
    const result = await this.crossfadeTo(next.nextTrack, {
      volume: session.volume,
      loop: false,
      onEnded: this._globalOnEndedBound,
    });

    if (!result?.success) {
      console.warn('[SoundscapeSession][ENDED] crossfade failed, fallback play()', result);

      await this.play(next.nextTrack, {
        volume: session.volume,
        loop: false,
        onEnded: this._globalOnEndedBound,
      });
    }

    // Preload following track immediately
    const following = this._getNextSessionTrack();
    if (following?.nextTrack) {
      this.preloadTrack(following.nextTrack);
    }
  } catch (error) {
    console.error('[SoundscapeSession][ENDED] transition error:', error);
    this.stop({ reason: 'session_transition_error' });
  }
}

async playSoundscapeSession({
  themeId,
  tracks = [],
  startIndex = 0,
  volume = 0.7,
} = {}) {
  const session = this.startSoundscapeSession({
    themeId,
    tracks,
    startIndex,
    volume,
    loop: false,
  });

  const track = session.currentTrack;

  if (!track) {
    console.error('[SoundscapeSession][PLAY] No track to play');
    return { success: false, reason: 'no_track' };
  }

  const result = await this.play(track, {
    volume,
    loop: false,
    onEnded: this._globalOnEndedBound,
  });

  if (result?.success) {
    const next = this._getNextSessionTrack();
    if (next?.nextTrack) {
      this.preloadTrack(next.nextTrack);
    }
  }

  return result;
}

  // ========================================================================
  // MAIN PLAY METHOD - iOS SAFARI OPTIMIZED
  // ========================================================================
  
  /**
   * Play a soundscape track
   * CRITICAL: This method is designed for iOS Safari compatibility.
   * The audioContext.resume() call happens synchronously in the user gesture.
   * 
   * @param {Object} track - Track object with file/path, name, category
   * @param {Object} options - { volume, loop, onEnded }
   * @returns {Object} - { success: boolean, reason?: string, requestId?: number }
   */
  async play(track, options = {}) {
    const { volume = 0.7, loop = false, onEnded = null } = options;
    const diag = typeof window !== 'undefined' ? window.__playlistDiag : null;
    
    console.log(`[SoundscapeService][PLAY] ▶️ play() called track=${track?.name || 'unknown'}`);
    console.log(`[SoundscapeService][PLAY]   isStarting=${this.isStarting} playbackState=${this.playbackState}`);
    
    if (diag) diag.steps.push({ step: '5_svc_play_enter', ts: performance.now() });
    
    // ═══════════════════════════════════════════════════════════════════════
    // SINGLE-FLIGHT LOCK: Prevent double-starts
    // ═══════════════════════════════════════════════════════════════════════
    if (this.isStarting) {
      console.log(`[SoundscapeService][PLAY] ⛔ IGNORED - isStarting=true (reqId=${this.startRequestId})`);
      return { success: false, reason: 'already_starting' };
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // ACQUIRE LOCK + INCREMENT REQUEST ID
    // ═══════════════════════════════════════════════════════════════════════
    this.isStarting = true;
    this.startRequestId++;
    const myRequestId = this.startRequestId;
    this.setState('LOADING', 'play() called');
    
    // Emit LOADING immediately
    this.notifyStateChange('loading', myRequestId);
    
    // ═══════════════════════════════════════════════════════════════════════
    // WATCHDOG: Force unlock after 15s
    // ═══════════════════════════════════════════════════════════════════════
    this._clearWatchdog();
    this._startWatchdogTimer = setTimeout(() => {
      if (this.isStarting && this.startRequestId === myRequestId) {
        console.error(`[SoundscapeService][PLAY] ⏱️ WATCHDOG TIMEOUT - forcing ERROR state`);
        this._forceErrorState('watchdog_timeout', myRequestId);
      }
    }, 15000);
    
    try {
      // ═══════════════════════════════════════════════════════════════════════
      // STEP 1: Initialize AudioContext (must happen in gesture)
      // ═══════════════════════════════════════════════════════════════════════
      if (diag) diag.steps.push({ step: '5a_init_start', ts: performance.now() });
      this.initialize();
      if (diag) diag.steps.push({ step: '5b_init_done', ts: performance.now() });
      
      // Acquire audio session (fire-and-forget to not break gesture)
      audioSessionManager.acquireSession('soundscape').catch(e => {
        console.warn('[SoundscapeService] Session acquisition issue (non-blocking):', e);
      });
      
      // ═══════════════════════════════════════════════════════════════════════
      // STEP 2: Resume AudioContext SYNCHRONOUSLY in gesture
      // This is the CRITICAL step for iOS Safari
      // ═══════════════════════════════════════════════════════════════════════
      if (this.audioContext.state === 'suspended') {
        if (diag) diag.steps.push({ step: '5c_resume_ctx_start', ts: performance.now() });
        console.log('[SoundscapeService][PLAY] 🔊 Resuming AudioContext (iOS gesture)...');
        await resumeAudioContext(this.audioContext);
        if (diag) diag.steps.push({ step: '5d_resume_ctx_done', ts: performance.now() });
        console.log(`[SoundscapeService][PLAY] 🔊 AudioContext state: ${this.audioContext.state}`);
      }
      
      // Check if superseded
      if (this.startRequestId !== myRequestId) {
        console.log(`[SoundscapeService][PLAY] ⛔ ABORT - reqId changed`);
        this._clearWatchdog();
        return { success: false, reason: 'superseded' };
      }
      
      // ═══════════════════════════════════════════════════════════════════════
      // STEP 3: Stop any current playback
      // ═══════════════════════════════════════════════════════════════════════
      if (diag) diag.steps.push({ step: '5e_stop_current', ts: performance.now() });
      this._stopCurrentPlayback();
      
      // ═══════════════════════════════════════════════════════════════════════
      // STEP 4: Build audio URL
      // ═══════════════════════════════════════════════════════════════════════
      if (diag) diag.steps.push({ step: '6_build_url_start', ts: performance.now() });
      const audioUrl = this._buildAudioUrl(track);
      if (diag) diag.steps.push({ step: '6_build_url_done', ts: performance.now(), file: audioUrl?.split('/').pop() });
      if (!audioUrl) {
        console.error(`[SoundscapeService][PLAY] ❌ Could not build audio URL`);
        this._forceErrorState('invalid_url', myRequestId);
        return { success: false, reason: 'invalid_url' };
      }
      
      console.log(`[SoundscapeService][PLAY] 🔗 URL: ${audioUrl}`);
      
      // Store pending playback info, but do NOT commit currentTrack yet
this.currentCategory = track.category;
this.currentVolume = volume;
this.loop = loop;
this.onEndedCallback = onEnded;

// ═══════════════════════════════════════════════════════════════════════
// STEP 5: Start playback — always use Web Audio API (fetch + decodeAudioData)
// The HTMLMediaElement (<audio>) path fails in Chrome/Chromium for M4A files
// (FFmpegDemuxer: no supported streams). Web Audio API decodes all formats.
// ═══════════════════════════════════════════════════════════════════════
await this._iosPlay(audioUrl, volume, loop, onEnded, myRequestId);

// Check if superseded / stopped / invalidated during loading
if (this.startRequestId !== myRequestId) {
  console.log(`[SoundscapeService][PLAY] ⛔ ABORT after playback start - reqId changed`);
  this._clearWatchdog();
  return { success: false, reason: 'superseded' };
}

// Hard guard: never commit PLAYING after a stop/cleanup/abort
if (this.playbackState === 'IDLE') {
  console.log(`[SoundscapeService][PLAY] ⛔ ABORT commit - playbackState is IDLE`);
  this._clearWatchdog();
  return { success: false, reason: 'stopped_before_commit' };
}

// Commit track only now, once playback is truly valid
this.currentTrack = track;

// ═══════════════════════════════════════════════════════════════════════
// SUCCESS: Emit PLAYING state
// ═══════════════════════════════════════════════════════════════════════
console.log(`[SoundscapeService][PLAY] ✅ PLAYING reqId=${myRequestId}`);
this._clearWatchdog();
this.isPlaying = true;
this.isPaused = false;
this.setState('PLAYING', 'playback started');
audioSessionManager.setPlaying('soundscape', true);
this.notifyStateChange('playing', myRequestId);
this.notifyTrackChange();

// Setup MediaSession for lock screen / background controls
this._setupMediaSession(track);
      
      // V1.1.2: Activate background keepalive on iOS Safari (in user gesture context)
      if (this.isIOS) {
        backgroundAudioHelper.activate();
      }
      return { success: true, requestId: myRequestId };
      
    } catch (error) {
      console.error(`[SoundscapeService][PLAY] ❌ ERROR reqId=${myRequestId}:`, error);
      this._forceErrorState(error.message || 'unknown_error', myRequestId);
      return { success: false, reason: error.message };
      
    } finally {
      this._clearWatchdog();
      if (this.startRequestId === myRequestId && this.isStarting) {
        console.log(`[SoundscapeService][PLAY] 🔓 Finally: unlocking isStarting`);
        this.isStarting = false;
      }
    }
  }
  
  // ========================================================================
  // V1.1.1: PRELOAD PIPELINE — fetch + decode next track in advance
  // ========================================================================

  /**
   * Preload a track by URL. Fetches + decodes the AudioBuffer into _preloadCache.
   * Call this as soon as the current track starts so the next buffer is ready
   * well before the current track ends.
   *
   * @param {Object} track - Track object (same shape as play())
   * @returns {Promise<boolean>} true if preload succeeded
   */
  async preloadTrack(track) {
    if (!track) return false;

    // Auto-initialize AudioContext if needed (will be suspended on iOS until gesture)
    if (!this.audioContext) {
      this.initialize();
    }
    if (!this.audioContext) return false;

    const url = this._buildAudioUrl(track);
    if (!url) return false;

    const trackId = track.id || track.path || url;

    // Already preloaded this exact track?
    if (this._preloadCache && this._preloadCache.url === url && this._preloadCache.buffer) {
      console.log(`[Preload] Cache HIT for ${trackId.slice(-30)}`);
      return true;
    }

    // Cancel any in-flight preload
    this._abortPreload();
    this._preloading = true;
    this._preloadController = new AbortController();
    const ctrl = this._preloadController;

    const t0 = performance.now();
    console.log(`[Preload] START fetch+decode for ${url.split('/').pop()}`);

    try {
      const resp = await fetch(url, {
        signal: ctrl.signal,
        mode: 'cors',
        credentials: 'omit'
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      const fetchDone = performance.now();
      const arrayBuf = await resp.arrayBuffer();
      const fetchMs = Math.round(performance.now() - t0);

      const decodeStart = performance.now();
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuf);
      const decodeMs = Math.round(performance.now() - decodeStart);

      if (ctrl.signal.aborted) return false; // cancelled while decoding

      this._preloadCache = {
        url,
        buffer: audioBuffer,
        trackId,
        track,
        readyAt: performance.now(),
        fetchMs,
        decodeMs
      };
      this._preloading = false;
      console.log(`[Preload] READY in ${fetchMs + decodeMs}ms (fetch=${fetchMs}ms decode=${decodeMs}ms) dur=${audioBuffer.duration.toFixed(1)}s`);
      return true;
    } catch (e) {
      this._preloading = false;
      if (e.name === 'AbortError') {
        console.log('[Preload] Aborted (new preload or stop)');
      } else {
        console.warn('[Preload] Failed:', e.message);
      }
      return false;
    }
  }

  /**
   * Get preloaded AudioBuffer if it matches the requested track URL.
   * Returns { buffer, fetchMs, decodeMs } or null.
   */
  getPreloadedBuffer(track) {
    if (!this._preloadCache || !track) return null;
    // If no audioContext yet, can't build URL to compare
    if (!this.audioContext) return null;
    const url = this._buildAudioUrl(track);
    if (this._preloadCache.url === url && this._preloadCache.buffer) {
      return this._preloadCache;
    }
    return null;
  }

  /** Cancel any in-flight preload fetch. */
  _abortPreload() {
    if (this._preloadController) {
      this._preloadController.abort();
      this._preloadController = null;
    }
    this._preloading = false;
  }

  /** Clear preload cache (on stop). */
  _clearPreloadCache() {
    this._abortPreload();
    this._preloadCache = null;
  }

  // ========================================================================
  // V2: CROSSFADE ENGINE — smooth volume transitions between tracks
  // ========================================================================

  /**
   * Crossfade from the currently playing track to a new track.
   * Uses preloaded AudioBuffer for instant start. Both iOS and desktop
   * use AudioBufferSourceNode for the new track (avoids audioElement.src latency).
   *
   * @param {Object} track - Next track object
   * @param {Object} options - { volume, onEnded, loop }
   * @returns {Object} { success: boolean, reason?: string }
   */
  async crossfadeTo(track, options = {}) {
    const { volume = this.currentVolume, onEnded = null, loop = false } = options;

    if (!this.audioContext || this.audioContext.state !== 'running') {
      console.warn('[Crossfade] AudioContext not running, falling back to play()');
      return this.play(track, { volume, onEnded });
    }

    const url = this._buildAudioUrl(track);
    if (!url) return { success: false, reason: 'invalid_url' };

    const transitionT0 = performance.now();
    const ctx = this.audioContext;
    const now = ctx.currentTime;
    const fadeDuration = this._crossfadeDuration;

    // --- Get AudioBuffer (preloaded fast-path or fetch inline) ---
    let audioBuffer, fromCache = false, fetchMs = 0, decodeMs = 0;
    const trackId = track.id || track.file || track.path || url;

    const cached =
      this._preloadCache &&
      this._preloadCache.trackId === trackId &&
      this._preloadCache.buffer
        ? this._preloadCache
        : null;

    console.log('[Crossfade][CACHE CHECK]', {
      expectedTrackId: trackId,
      cachedTrackId: this._preloadCache?.trackId,
      cachedUrl: this._preloadCache?.url,
      currentUrl: url,
      hit: !!cached,
    });

    if (cached) {
      audioBuffer = cached.buffer;
      fetchMs = cached.fetchMs;
      decodeMs = cached.decodeMs;
      fromCache = true;
      this._preloadCache = null;
      console.log(`[Crossfade] PRELOAD HIT — buffer ready (was ${fetchMs + decodeMs}ms)`);
    } else {
      console.log(`[Crossfade] No preload — fetching inline...`);
      try {
        const ft0 = performance.now();
        const resp = await fetch(url, { mode: 'cors', credentials: 'omit' });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const arrayBuf = await resp.arrayBuffer();
        fetchMs = Math.round(performance.now() - ft0);
        const dt0 = performance.now();
        audioBuffer = await ctx.decodeAudioData(arrayBuf);
        decodeMs = Math.round(performance.now() - dt0);
      } catch (e) {
        console.error('[Crossfade] Fetch/decode error:', e.message);
        return { success: false, reason: e.message };
      }
    }

    // V1.2 FIX: After async fetch/decode, check if stop() was called during the await
    if (this.playbackState === 'IDLE' || this.playbackState === 'ERROR') {
      console.log('[Crossfade] Service was stopped during fetch, aborting crossfade');
      return { success: false, reason: 'stopped_during_fetch' };
    }

    // --- FADE OUT current source ---
    // Clear iOS safety timer to prevent stale onEnded callback
    if (this._iosSafetyTimer) {
      clearTimeout(this._iosSafetyTimer);
      this._iosSafetyTimer = null;
    }
    // 1) Buffer source (iOS, or desktop after a previous crossfade)
    if (this.soundSource && this.soundGainNode) {
      this.soundSource.onended = null; // prevent stale callback
      this.soundGainNode.gain.cancelScheduledValues(now);
      this.soundGainNode.gain.setValueAtTime(this.soundGainNode.gain.value, now);
      this.soundGainNode.gain.linearRampToValueAtTime(0, now + fadeDuration);
      this._outgoingSource = this.soundSource;
      this._outgoingGain = this.soundGainNode;
    }
    // 2) audioElement (desktop first track before any crossfade)
    else if (!this.isIOS && this.mediaSourceGain) {
      if (this._currentEndedHandler) {
        this.audioElement.removeEventListener('ended', this._currentEndedHandler);
        this._currentEndedHandler = null;
      }
      if (this._endSafetyHandler) {
        this.audioElement.removeEventListener('timeupdate', this._endSafetyHandler);
        this._endSafetyHandler = null;
      }
      this.mediaSourceGain.gain.cancelScheduledValues(now);
      this.mediaSourceGain.gain.setValueAtTime(this.mediaSourceGain.gain.value, now);
      this.mediaSourceGain.gain.linearRampToValueAtTime(0, now + fadeDuration);
    }

    // --- CREATE new source (Track B) via AudioBufferSourceNode ---
    const newGain = ctx.createGain();
    newGain.gain.setValueAtTime(0.001, now);

    const newSource = ctx.createBufferSource();
    newSource.buffer = audioBuffer;
    newSource.loop = loop;

    newSource.connect(newGain);
    newGain.connect(this.inputNode);

    // Attach onEnded for Track B
    this.onEndedCallback = onEnded;
    this.loop = loop;
    newSource.onended = () => {
      if (newSource._stopped) return;
      if (!this.isPaused && !loop && onEnded) {
        console.log('[Crossfade] Track B ended naturally, calling onEnded');
        onEnded();
      }
    };

    // Start Track B and fade in
    newSource.start(0);
    newGain.gain.linearRampToValueAtTime(volume, now + fadeDuration);

    // Store as current
    this.soundBuffer = audioBuffer;
    this.soundSource = newSource;
    this.soundGainNode = newGain;
    this.iosStartTime = now;

    this.currentTrackDuration = audioBuffer.duration;
    this.iosDuration = audioBuffer.duration;

    console.log('[Crossfade] Track B duration set', {
      duration: audioBuffer.duration,
    });

    // Update track info
    this.currentTrack = track;
    this.currentCategory = track.category;
    this.currentVolume = volume;
    this.loop = false;

    // Notify UI
    this.notifyTrackChange();
    this._setupMediaSession(track);

    // Schedule cleanup of outgoing source after crossfade completes
    if (this._outgoingCleanupTimer) clearTimeout(this._outgoingCleanupTimer);
    this._outgoingCleanupTimer = setTimeout(() => {
      this._cleanupOutgoing();
    }, (fadeDuration + 0.5) * 1000);

    // Record metric
    const gapMs = Math.round(performance.now() - transitionT0);
    this._recordTransitionMetric({
      trackName: track.name || url.split('/').pop(),
      fetchMs, decodeMs,
      readyBeforeEndMs: null,
      startGapMs: gapMs,
      fromCache
    });

    console.log(`[Crossfade] Started: "${track.name}" fade=${fadeDuration}s fromCache=${fromCache} gap=${gapMs}ms`);
    return { success: true };
  }

  /** Clean up the outgoing source after crossfade completes. */
  _cleanupOutgoing() {
    if (this._outgoingSource) {
      try {
        this._outgoingSource.onended = null;
        this._outgoingSource._stopped = true;
        this._outgoingSource.stop();
        this._outgoingSource.disconnect();
      } catch (e) { /* already stopped */ }
      this._outgoingSource = null;
    }
    if (this._outgoingGain) {
      try { this._outgoingGain.disconnect(); } catch (e) {}
      this._outgoingGain = null;
    }
    // If desktop audioElement was the outgoing source, pause it
    if (!this.isIOS && this.audioElement) {
      try {
        this.audioElement.pause();
        this.audioElement.removeAttribute('src');
      } catch (e) {}
    }
    console.log('[Crossfade] Outgoing source cleaned up');
  }

  // ========================================================================
  // V1.1.1: TRANSITION METRICS — per-transition instrumentation
  // ========================================================================

  /**
   * Record a transition's timing breakdown.
   * @param {Object} m - { fetchMs, decodeMs, readyBeforeEndMs, startGapMs, trackName, fromCache }
   */
  _recordTransitionMetric(m) {
    const entry = { ...m, ts: Date.now() };
    this._transitionMetrics.push(entry);
    this._lastMetric = entry; // Expose for diagnostic access
    if (this._transitionMetrics.length > 20) this._transitionMetrics.shift();

    const ok = (m.readyBeforeEndMs === null || m.readyBeforeEndMs > 800) && m.startGapMs < 50;
    const tag = ok ? '✅' : '⚠️';
    console.log(
      `[Transition] ${tag} "${m.trackName}" fetch=${m.fetchMs}ms decode=${m.decodeMs}ms ` +
      `readyBefore=${m.readyBeforeEndMs !== null ? m.readyBeforeEndMs + 'ms' : 'N/A'} ` +
      `gap=${m.startGapMs}ms cache=${m.fromCache}`
    );

    // Notify debug overlay listeners
    this._metricsListeners.forEach(cb => { try { cb(entry); } catch (_) {} });
  }

  /** Subscribe to transition metrics (for debug overlay). Returns unsubscribe fn. */
  onTransitionMetric(cb) {
    this._metricsListeners.push(cb);
    return () => { this._metricsListeners = this._metricsListeners.filter(f => f !== cb); };
  }

  /** Get recent transition metrics. */
  getTransitionMetrics() {
    return [...this._transitionMetrics];
  }

  /** Get current track duration (buffer or HTMLMediaElement). */
getCurrentTrackDuration() {
  console.log('[DURATION GETTER]', {
    soundBuffer: this.soundBuffer,
    soundBufferDuration: this.soundBuffer?.duration,
    currentTrackDuration: this.currentTrackDuration,
    iosDuration: this.iosDuration,
    audioElementDuration: this.audioElement?.duration,
  });

  if (this.soundBuffer?.duration) {
    return this.soundBuffer.duration;
  }

  if (this.currentTrackDuration) {
    return this.currentTrackDuration;
  }

  if (this.iosDuration) {
    return this.iosDuration;
  }

  if (this.audioElement?.duration && Number.isFinite(this.audioElement.duration)) {
    return this.audioElement.duration;
  }

  return 0;
}
  /** Get current playback position in seconds. */
  getCurrentTrackPosition() {
    if (this.audioContext && this.iosStartTime) {
      return Math.max(0, this.audioContext.currentTime - this.iosStartTime);
    }

    if (this.audioElement?.currentTime) {
      return this.audioElement.currentTime;
    }

    return 0;
  }
  // ========================================================================
  // iOS PLAYBACK (Web Audio Buffer Engine)
  // ========================================================================
  
  async _iosPlay(url, volume, loop, onEnded, reqId) {
    console.log('[IOS PLAY ENTER TEST]', {
      url,
      volume,
      loop,
      reqId,
    });
    console.log(`[SoundscapeService][iOS] Fetching and decoding: ${url.split('/').pop()}`);
    const diag = typeof window !== 'undefined' ? window.__playlistDiag : null;
    
    // Cancel any pending fetch (but NOT the preload — it's separate)
    if (this.fetchController) {
      this.fetchController.abort();
    }
    this.fetchController = new AbortController();

    // V1.1.1: Check preload cache FIRST
    const cached =
      this._preloadCache &&
      this._preloadCache.url === url &&
      this._preloadCache.buffer
        ? this._preloadCache
        : null;

    const transitionT0 = performance.now();
    let fetchMs = 0, decodeMs = 0, fromCache = false;
    
    try {
      let audioBuffer;

      if (cached) {
        // ── FAST PATH: preloaded buffer ready ──
        audioBuffer = cached.buffer;
        fetchMs = cached.fetchMs;
        decodeMs = cached.decodeMs;
        fromCache = true;
        if (diag) diag.steps.push({ step: '7_cache_hit', ts: performance.now() });
        console.log(`[SoundscapeService][iOS] PRELOAD HIT — skipping fetch+decode (was ${fetchMs + decodeMs}ms)`);
        this._preloadCache = null; // consumed
      } else {
        // ── SLOW PATH: fetch + decode inline ──
        if (diag) diag.steps.push({ step: '7_fetch_start', ts: performance.now() });
        console.log(`[SoundscapeService][iOS] Fetching: ${url}`);
        const fetchT0 = performance.now();
        const response = await fetch(url, {
          signal: this.fetchController.signal,
          mode: 'cors',
          credentials: 'omit'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const arrayBuffer = await response.arrayBuffer();
        fetchMs = Math.round(performance.now() - fetchT0);
        if (diag) diag.steps.push({ step: '8_fetch_done', ts: performance.now(), bytes: arrayBuffer.byteLength, fetchMs });
        console.log(`[SoundscapeService][iOS] Fetched ${arrayBuffer.byteLength} bytes in ${fetchMs}ms`);
        
        if (diag) diag.steps.push({ step: '9_decode_start', ts: performance.now() });
        const decodeT0 = performance.now();
        audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
        decodeMs = Math.round(performance.now() - decodeT0);
        if (diag) diag.steps.push({ step: '9_decode_done', ts: performance.now(), duration: audioBuffer.duration.toFixed(1), decodeMs });
        console.log(`[SoundscapeService][iOS] Decoded: ${audioBuffer.duration.toFixed(1)}s in ${decodeMs}ms`);
      }
      // 🔥 CRITIQUE : rendre le 1er track compatible crossfade
      this.soundBuffer = audioBuffer;
      this.currentTrackDuration = audioBuffer.duration;
      this.iosDuration = audioBuffer.duration;

      console.log('[iOS] Track duration set', {
        duration: audioBuffer.duration,
      });
      
      this.soundBuffer = audioBuffer;
      this.currentTrackDuration = audioBuffer.duration;
      this.iosDuration = audioBuffer.duration;

      console.log('[iOS] Track duration set', {
      duration: audioBuffer.duration,
        soundBufferDuration: this.soundBuffer?.duration,
      });

      console.log('[IOS PLAY BEFORE REQID CHECK]', {
        hasAudioBuffer: !!audioBuffer,
        duration: audioBuffer?.duration,
      });

      // Check if superseded during fetch
      if (this.startRequestId !== reqId) {
        console.log(`[SoundscapeService][iOS] ABORT - reqId changed during fetch`);
        return;
      }
      
      // Store buffer for pause/resume
      this.soundBuffer = audioBuffer;
      
      // Record when this track actually started
      const startGapMs = Math.round(performance.now() - transitionT0);
      this._currentTrackStartCtx = this.audioContext.currentTime;
      
      // Create and connect nodes
      this._iosPlayBuffer(audioBuffer, volume, loop, onEnded, 0);
      
      // V1.1.1: Record transition metric
      this._recordTransitionMetric({
        trackName: this.currentTrack?.name || url.split('/').pop(),
        fetchMs,
        decodeMs,
        readyBeforeEndMs: null, // Computed by caller (TherapyPlayer) if preload was used
        startGapMs,
        fromCache
      });
      
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('[SoundscapeService][iOS] Fetch aborted');
        return;
      }
      console.error('[SoundscapeService][iOS] Fetch/Decode error:', error.message);
      throw error;
    }
  }
  
  _iosPlayBuffer(buffer, volume, loop, onEnded, offset = 0) {
    const diag = typeof window !== 'undefined' ? window.__playlistDiag : null;
    if (diag) diag.steps.push({ step: '10a_iosPlayBuffer_enter', ts: performance.now() });
    
    // Stop current source (with fade-out)
    this._iosStopCurrentBuffer(offset > 0);
    
    // Create gain node — start at 0 for fade-in
    this.soundGainNode = this.audioContext.createGain();
    this.soundGainNode.gain.setValueAtTime(0.001, this.audioContext.currentTime);
    
    // Create buffer source
    this.soundSource = this.audioContext.createBufferSource();
    this.soundSource.buffer = buffer;
    this.soundSource.loop = loop;
    
    if (diag) diag.steps.push({ step: '10b_nodes_created', ts: performance.now() });
    
    // Connect: source -> gain -> input -> master -> destination
    this.soundSource.connect(this.soundGainNode);
    this.soundGainNode.connect(this.inputNode);
    
    if (diag) diag.steps.push({ step: '10c_chain_connected', ts: performance.now() });
    
    // Handle ended event
    this.soundSource.onended = () => {
      // V1.1.1: Mark source as stopped for resume detection
      if (this.soundSource) this.soundSource._stopped = true;
      if (!this.isPaused && !loop && onEnded) {
        console.log('[SoundscapeService][iOS] Sound ended, calling onEnded');
        // Clear safety timer
        if (this._iosSafetyTimer) {
          clearTimeout(this._iosSafetyTimer);
          this._iosSafetyTimer = null;
        }
        onEnded();
      }
    };
    
    // Safety: schedule a forced callback slightly after expected end
    if (!loop && onEnded && buffer.duration > 1) {
      const safetyDelay = ((buffer.duration - offset) * 1000) + 2000; // 2s after expected end
      if (this._iosSafetyTimer) clearTimeout(this._iosSafetyTimer);
      this._iosSafetyTimer = setTimeout(() => {
        if (this.soundSource && !this.isPaused && !this.soundSource._stopped) {
          console.log('[SoundscapeService][iOS] Safety: onended did not fire, forcing callback');
          if (this.soundSource) this.soundSource._stopped = true;
          onEnded();
        }
      }, safetyDelay);
    }
    
    // Track timing for pause/resume
    this.iosStartTime = this.audioContext.currentTime - offset;
    
    // Start playback
    try {
      this.soundSource.start(0, offset);
      if (diag) diag.steps.push({ step: '10d_source_started', ts: performance.now() });
      // V1.1.1: Fade-in over 350ms (avoids clicks/pops)
      this.soundGainNode.gain.linearRampToValueAtTime(volume, this.audioContext.currentTime + 0.35);
      
      // V1.1.1: Schedule fade-out 450ms before track ends (only non-looping)
      if (!loop && buffer.duration > 1) {
        const fadeOutAt = this.audioContext.currentTime + (buffer.duration - offset) - 0.45;
        if (fadeOutAt > this.audioContext.currentTime + 0.5) {
          this.soundGainNode.gain.setValueAtTime(volume, fadeOutAt);
          this.soundGainNode.gain.linearRampToValueAtTime(0, fadeOutAt + 0.45);
        }
      }
      
      console.log(`[SoundscapeService][iOS] Buffer started (loop=${loop}, vol=${(volume*100).toFixed(0)}%, offset=${offset.toFixed(2)}s, fadeIn=350ms)`);
    } catch (error) {
      console.error('[SoundscapeService][iOS] BufferSource start failed:', error);
      throw error;
    }
  }
  
  _iosStopCurrentBuffer(isResume = false) {
    if (this._iosSafetyTimer) {
      clearTimeout(this._iosSafetyTimer);
      this._iosSafetyTimer = null;
    }
    if (this.soundSource) {
      try {
        this.soundSource.onended = null;
        this.soundSource.stop();
        this.soundSource.disconnect();
      } catch (e) {
        // Already stopped
      }
      this.soundSource = null;
    }
    if (this.soundGainNode) {
      try {
        this.soundGainNode.disconnect();
      } catch (e) {}
      this.soundGainNode = null;
    }
    
    if (!isResume) {
      this.iosPausedAt = 0;
      this.iosStartTime = 0;
    }
  }
  
  // ========================================================================
  // NON-iOS PLAYBACK (HTMLMediaElement)
  // ========================================================================
  
  async _nonIosPlay(url, volume, loop, onEnded, reqId) {
    console.log(`[SoundscapeService][NonIOS] Playing: ${url.split('/').pop()}`);
    const diag = typeof window !== 'undefined' ? window.__playlistDiag : null;
    const transitionT0 = performance.now();
    if (diag) diag.steps.push({ step: '7_nonios_setup_start', ts: performance.now() });
    
    // Remove old event listener
    if (this._currentEndedHandler) {
      this.audioElement.removeEventListener('ended', this._currentEndedHandler);
      this._currentEndedHandler = null;
    }
    if (this._endSafetyHandler) {
      this.audioElement.removeEventListener('timeupdate', this._endSafetyHandler);
      this._endSafetyHandler = null;
    }
    
    // Reset element and its gain
    this.audioElement.pause();
    this.audioElement.currentTime = 0;
    this.audioElement.loop = loop;
    this.audioElement.src = url;
    
    // Restore mediaSourceGain to full (may have been faded out by crossfade)
    if (this.mediaSourceGain && this.audioContext) {
      const now = this.audioContext.currentTime;
      this.mediaSourceGain.gain.cancelScheduledValues(now);
      this.mediaSourceGain.gain.setValueAtTime(1.0, now);
    }
    
    // Attach onEnded if provided
    if (onEnded && !loop) {
      this._currentEndedHandler = () => {
        console.log('[SoundscapeService][NonIOS] ended event fired');
        // Remove safety timeupdate listener
        if (this._endSafetyHandler) {
          this.audioElement.removeEventListener('timeupdate', this._endSafetyHandler);
          this._endSafetyHandler = null;
        }
        onEnded();
      };
      this.audioElement.addEventListener('ended', this._currentEndedHandler);
      
      // Safety mechanism: detect end-of-track via timeupdate in case 'ended' doesn't fire
      if (this._endSafetyHandler) {
        this.audioElement.removeEventListener('timeupdate', this._endSafetyHandler);
      }
      this._endSafetyHandler = () => {
        const el = this.audioElement;
        if (el.duration && el.currentTime >= el.duration - 0.3 && !el.loop) {
          console.log('[SoundscapeService][NonIOS] Safety: track near end, triggering callback');
          el.removeEventListener('timeupdate', this._endSafetyHandler);
          this._endSafetyHandler = null;
          if (this._currentEndedHandler) {
            el.removeEventListener('ended', this._currentEndedHandler);
            this._currentEndedHandler = null;
          }
          onEnded();
        }
      };
      this.audioElement.addEventListener('timeupdate', this._endSafetyHandler);
    }
    
    try {
      if (diag) diag.steps.push({ step: '8_nonios_play_call', ts: performance.now() });
      await this.audioElement.play();
      const gapMs = Math.round(performance.now() - transitionT0);
      if (diag) diag.steps.push({ step: '10d_nonios_playing', ts: performance.now() });
      console.log(`[SoundscapeService][NonIOS] Playback started in ${gapMs}ms`);
      
      this._recordTransitionMetric({
        trackName: this.currentTrack?.name || url.split('/').pop(),
        fetchMs: 0, decodeMs: 0,
        readyBeforeEndMs: null,
        startGapMs: gapMs,
        fromCache: false
      });
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('[SoundscapeService][NonIOS] Play aborted');
        return;
      }
      throw error;
    }
  }
  
  // ========================================================================
  // PAUSE / RESUME
  // ========================================================================
  
  pause() {
    console.log(`[SoundscapeService] ⏸️ pause() called, state=${this.playbackState}`);
    
    if (this.playbackState !== 'PLAYING') {
      console.log('[SoundscapeService] Not playing, nothing to pause');
      return false;
    }
    
    audioSessionManager.setUserPaused('soundscape', true);
    
    // V2: Buffer-based pause (iOS, or desktop after crossfade)
    if (this.soundSource && this.soundBuffer) {
      const elapsed = this.audioContext.currentTime - this.iosStartTime;
      this.iosPausedAt = this.loop 
        ? elapsed % this.soundBuffer.duration
        : Math.min(elapsed, this.soundBuffer.duration);
      
      console.log(`[SoundscapeService] Pausing buffer at ${this.iosPausedAt.toFixed(2)}s`);
      this.isPaused = true;
      this._iosStopCurrentBuffer(true);
    }
    // audioElement-based pause (desktop first track before any crossfade)
    else if (!this.isIOS && this.audioElement && !this.audioElement.paused) {
      this.audioElement.pause();
      console.log(`[SoundscapeService][NonIOS] Paused at ${this.audioElement.currentTime.toFixed(2)}s`);
    }
    
    this.isPlaying = false;
    this.isPaused = true;
    this.setState('PAUSED', 'user pause');
    this.notifyStateChange('paused');
    
    return true;
  }
  
  resume() {
    console.log(`[SoundscapeService] ▶️ resume() called, state=${this.playbackState}`);
    
    if (this.playbackState !== 'PAUSED') {
      console.log('[SoundscapeService] Not paused, nothing to resume');
      return false;
    }
    
    audioSessionManager.setUserPaused('soundscape', false);
    
    // V2: Buffer-based resume (iOS, or desktop after crossfade)
    if (this.soundBuffer) {
      console.log(`[SoundscapeService] Resuming buffer from ${this.iosPausedAt.toFixed(2)}s`);
      this._iosPlayBuffer(
        this.soundBuffer,
        this.currentVolume,
        this.loop,
        this.onEndedCallback,
        this.iosPausedAt
      );
    }
    // audioElement-based resume (desktop first track)
    else if (!this.isIOS && this.audioElement && this.audioElement.paused) {
      this.audioElement.play()
        .then(() => console.log('[SoundscapeService][NonIOS] Resumed'))
        .catch(e => console.error('[SoundscapeService][NonIOS] Resume error:', e));
    }
    
    this.isPlaying = true;
    this.isPaused = false;
    this.setState('PLAYING', 'resumed');
    this.notifyStateChange('playing');
    
    return true;
  }
  
  async stopWithFade(durationMs = 150, options = {}) {
  const { reason = 'user' } = options;
  console.log(`[SoundscapeService] 🎚️ stopWithFade() called, reason=${reason}, duration=${durationMs}ms`);

  // Évite les fades concurrents
  if (this._stopFadeInProgress) {
    console.log('[SoundscapeService] stopWithFade already in progress');
    return;
  }

  this._stopFadeInProgress = true;

  try {
    const fadeMs = Math.max(80, durationMs);

    // iOS / Web Audio path
    if (this.soundGainNode?.gain && this.audioContext) {
      const now = this.audioContext.currentTime;
      const currentGain =
        typeof this.soundGainNode.gain.value === 'number'
          ? this.soundGainNode.gain.value
          : 1;

      try {
        this.soundGainNode.gain.cancelScheduledValues(now);
        this.soundGainNode.gain.setValueAtTime(currentGain, now);
        this.soundGainNode.gain.linearRampToValueAtTime(0.0001, now + fadeMs / 1000);
      } catch (e) {
        console.warn('[SoundscapeService] WebAudio fade failed, fallback to direct stop', e);
      }

      await new Promise(resolve => setTimeout(resolve, fadeMs + 20));
      this.stop({ reason });
      return;
    }

    // Desktop / HTMLAudioElement path
    if (!this.isIOS && this.audioElement) {
      const startVolume =
        typeof this.audioElement.volume === 'number' ? this.audioElement.volume : 1;

      const steps = 5;
      const stepDelay = Math.max(16, Math.floor(fadeMs / steps));

      for (let i = 1; i <= steps; i++) {
        const nextVolume = Math.max(0, startVolume * (1 - i / steps));
        try {
          this.audioElement.volume = nextVolume;
        } catch (e) {}
        await new Promise(resolve => setTimeout(resolve, stepDelay));
      }

      this.stop({ reason });

      try {
        this.audioElement.volume = startVolume;
      } catch (e) {}

      return;
    }

    // Fallback
    this.stop({ reason });
  } catch (err) {
    console.error('[SoundscapeService] stopWithFade failed, fallback to stop()', err);
    this.stop({ reason });
  } finally {
    this._stopFadeInProgress = false;
  }
}

  // ========================================================================
  // STOP
  // ========================================================================
  
  stop(options = {}) {
    const { reason = 'user' } = options;
    console.log(`[SoundscapeService] ⏹️ stop() called, reason=${reason}`);

    if (this.soundscapeSession?.isActive) {
  console.log('[SoundscapeSession][STOP]', {
    reason,
    sessionId: this.soundscapeSession.sessionId,
    themeId: this.soundscapeSession.themeId,
  });
}

this.soundscapeSession = {
  sessionId: null,
  isActive: false,
  themeId: null,
  tracks: [],
  currentIndex: 0,
  currentTrack: null,
  volume: this.currentVolume || 0.7,
  loop: false,
  startedAt: null,
};
    
    this._stopFadeInProgress = false;
    this._clearWatchdog();
    this.isStarting = false;
    
    // Cancel any fetch
    if (this.fetchController) {
      this.fetchController.abort();
      this.fetchController = null;
    }
    
    // Clear preload pipeline
    this._clearPreloadCache();
    
    // V2: Clean up crossfade outgoing source
    this._cleanupOutgoing();
    if (this._outgoingCleanupTimer) {
      clearTimeout(this._outgoingCleanupTimer);
      this._outgoingCleanupTimer = null;
    }
    
    // Stop buffer source (iOS or desktop after crossfade)
    if (this.soundSource) {
      this._iosStopCurrentBuffer();
      this.soundBuffer = null;
    }
    
    // Stop audioElement (desktop)
    if (!this.isIOS) {
      if (this._currentEndedHandler) {
        this.audioElement?.removeEventListener('ended', this._currentEndedHandler);
        this._currentEndedHandler = null;
      }
      if (this.audioElement) {
        this.audioElement.pause();
        try { this.audioElement.currentTime = 0; } catch (e) {}
        this.audioElement.removeAttribute('src');
      }
      // Reset mediaSourceGain
      if (this.mediaSourceGain && this.audioContext) {
        try {
          const now = this.audioContext.currentTime;
          this.mediaSourceGain.gain.cancelScheduledValues(now);
          this.mediaSourceGain.gain.setValueAtTime(1.0, now);
        } catch (e) {}
      }
    }
    
    this.isPlaying = false;
    this.isPaused = false;
    this.currentTrack = null;
    this.soundBuffer = null;
    this.setState('IDLE', 'stopped');
    
    audioSessionManager.setPlaying('soundscape', false, reason);
    audioSessionManager.releaseSession('soundscape');
    
    this.notifyStateChange('stopped');
  }
  
  // ========================================================================
  // VOLUME
  // ========================================================================
  
  setVolume(volume) {
    console.log(`[SoundscapeService] setVolume(${volume}) - usesSharedContext=${this.usesSharedContext}`);
    this.currentVolume = volume;
    
    // BUILD 66.3: Control volume via our inputNode gain
    if (this.inputNode && this.audioContext) {
      const now = this.audioContext.currentTime;
      this.inputNode.gain.cancelScheduledValues(now);
      this.inputNode.gain.setValueAtTime(this.inputNode.gain.value, now);
      this.inputNode.gain.linearRampToValueAtTime(volume, now + 0.05);
      console.log(`[SoundscapeService] Set inputNode.gain to ${volume}`);
    }
    
    // Also set audioService master volume for consistency
    if (this.usesSharedContext && audioService.masterGain && this.audioContext) {
      const now = this.audioContext.currentTime;
      audioService.masterGain.gain.cancelScheduledValues(now);
      audioService.masterGain.gain.setValueAtTime(audioService.masterGain.gain.value, now);
      audioService.masterGain.gain.linearRampToValueAtTime(volume, now + 0.05);
      console.log(`[SoundscapeService] Set audioService.masterGain to ${volume}`);
    }
    
    // V2: Control the per-sound gain node (buffer-based playback on iOS or desktop after crossfade)
    if (this.soundGainNode && this.audioContext) {
      const now = this.audioContext.currentTime;
      this.soundGainNode.gain.cancelScheduledValues(now);
      this.soundGainNode.gain.setValueAtTime(this.soundGainNode.gain.value, now);
      this.soundGainNode.gain.linearRampToValueAtTime(volume, now + 0.05);
      console.log(`[SoundscapeService] Set soundGainNode to ${volume}`);
    }
  }
  
  // ========================================================================
  // HELPERS
  // ========================================================================
  
  _buildAudioUrl(track) {
    const baseUrl = getBaseUrl();
    let url = track.file || track.path || track.audioUrl;
    
    console.log('[SoundscapeService][_buildAudioUrl] Input:', {
      file: track.file,
      path: track.path,
      audioUrl: track.audioUrl,
      resolved: url,
      baseUrl
    });
    
    if (!url) {
      console.error('[SoundscapeService] No file/path in track:', track);
      return null;
    }
    
    // Handle proxy URLs - already have /api/r2/stream/ prefix
    if (url.startsWith('/api/r2/stream/')) {
      const reencoded = reencodeAudioPath(url);
      url = `${baseUrl}${reencoded}`;
      console.log('[SoundscapeService][_buildAudioUrl] Proxy URL:', url);
    }
    // Handle other relative paths
    else if (url.startsWith('/')) {
      url = `${baseUrl}${url}`;
      console.log('[SoundscapeService][_buildAudioUrl] Relative URL:', url);
    }
    // Build URL from category + filename if needed (legacy)
    else if (!url.startsWith('http') && track.category) {
      const filename = url.split('/').pop();
      url = `${baseUrl}/api/r2/stream/${encodeURIComponent(track.category)}/${encodeURIComponent(filename)}`;
      console.log('[SoundscapeService][_buildAudioUrl] Built URL:', url);
    }
    // Already absolute URL
    else if (url.startsWith('http')) {
      console.log('[SoundscapeService][_buildAudioUrl] Absolute URL:', url);
    }
    
    console.log('[SoundscapeService][_buildAudioUrl] Final URL:', url);
    return url;
  }
  
  _stopCurrentPlayback() {
    // V2.2.4: Clean up event listeners FIRST to prevent stale callbacks
    if (this._currentEndedHandler && this.audioElement) {
      this.audioElement.removeEventListener('ended', this._currentEndedHandler);
      this._currentEndedHandler = null;
    }
    if (this._endSafetyHandler && this.audioElement) {
      this.audioElement.removeEventListener('timeupdate', this._endSafetyHandler);
      this._endSafetyHandler = null;
    }
    if (this._iosSafetyTimer) {
      clearTimeout(this._iosSafetyTimer);
      this._iosSafetyTimer = null;
    }
    // V3: Cancel any pending outgoing cleanup timer to prevent it killing new playback
    if (this._outgoingCleanupTimer) {
      clearTimeout(this._outgoingCleanupTimer);
      this._outgoingCleanupTimer = null;
    }
    // Stop buffer source if active (iOS or desktop after crossfade)
    if (this.soundSource) {
      this._iosStopCurrentBuffer();
    }
    // Stop audioElement on desktop
    if (!this.isIOS && this.audioElement) {
      this.audioElement.pause();
      try { this.audioElement.currentTime = 0; } catch (e) {}
    }
    // Clean up any in-progress crossfade
    this._cleanupOutgoing();
  }
  
  /**
   * V1.1.1: Resume playback after iOS Safari lock/unlock or app background/foreground.
   * Called by AudioSessionManager.onForeground().
   *
   * On iOS, locking the screen suspends the AudioContext and kills the
   * BufferSourceNode. We must:
   *   1. Resume the AudioContext
   *   2. Recreate the BufferSourceNode at the stored position
   *   3. Reconnect the audio graph
   *
   * If audioContext.resume() fails (no user gesture), we flag _needsGestureResume
   * so the next user tap triggers playback immediately.
   */
  async resumeIfNeeded() {
    console.log(`[SoundscapeService] resumeIfNeeded() state=${this.playbackState} isPaused=${this.isPaused} ctx=${this.audioContext?.state}`);

    // Don't resume if user explicitly paused
    if (this.isPaused) {
      console.log('[SoundscapeService] User-paused, skip resume');
      return false;
    }

    // Only resume if we were playing
    if (this.playbackState !== 'PLAYING') {
      console.log('[SoundscapeService] Not PLAYING, skip resume');
      return false;
    }

    try {
      // V1.1.2: Re-activate background keepalive on foreground resume
      if (this.isIOS) {
        backgroundAudioHelper.ensureActive();
      }

      // Step 1: Resume AudioContext (handle 'suspended', 'interrupted', or any non-running state)
      if (this.audioContext && this.audioContext.state !== 'running') {
        console.log(`[SoundscapeService] AudioContext state=${this.audioContext.state}, resuming...`);
        await this.audioContext.resume();
        // iOS may need a moment — retry once after short delay
        if (this.audioContext.state !== 'running') {
          await new Promise(r => setTimeout(r, 300));
          await this.audioContext.resume();
          console.log(`[SoundscapeService] AudioContext after retry: ${this.audioContext.state}`);
        }
        // V1.1.2: One more retry with longer delay on iOS Safari
        if (this.audioContext.state !== 'running' && this.isIOS) {
          await new Promise(r => setTimeout(r, 500));
          await this.audioContext.resume();
          console.log(`[SoundscapeService] AudioContext after 2nd retry: ${this.audioContext.state}`);
        }
        if (this.audioContext.state !== 'running') {
          console.warn('[SoundscapeService] AudioContext still not running, needs user gesture');
          this._needsGestureResume = true;
          // V1.1.2: Emit state change so UI can show a "tap to resume" indicator
          this.notifyStateChange('needs_gesture', 0);
          return false;
        }
      }

      // Step 2: Recreate BufferSourceNode after background resume (iOS or desktop after crossfade).
      if (this.soundBuffer) {
        let resumeOffset = 0;
        if (this._bgPosition > 0 && this._bgPosition < this.soundBuffer.duration) {
          resumeOffset = this._bgPosition;
        } else if (this.iosStartTime > 0 && this.audioContext.currentTime > this.iosStartTime) {
          const elapsed = this.audioContext.currentTime - this.iosStartTime;
          resumeOffset = this.loop
            ? elapsed % this.soundBuffer.duration
            : Math.min(elapsed, this.soundBuffer.duration);
        }

        console.log(`[SoundscapeService] Recreating buffer source at offset=${resumeOffset.toFixed(2)}s`);
        this._iosPlayBuffer(
          this.soundBuffer,
          this.currentVolume,
          this.loop,
          this.onEndedCallback,
          resumeOffset
        );
        this._bgPosition = 0;
        console.log('[SoundscapeService] Buffer source recreated after foreground resume');
        return true;
      }

      // Step 3 (non-iOS, no buffer): Resume HTMLMediaElement
      if (!this.isIOS && this.audioElement && this.audioElement.paused && !this.audioElement.ended) {
        await this.audioElement.play();
        console.log('[SoundscapeService] Non-iOS audio resumed');
      }

      return true;
    } catch (error) {
      console.error('[SoundscapeService] resumeIfNeeded error:', error);
      return false;
    }
  }

  /**
   * V1.1.1: Store playback position when going to background.
   * Called by the lifecycle handlers before the AudioContext gets suspended.
   */
  storeBackgroundPosition() {
    if (!this.isPlaying || this.isPaused) return;

    // V2: Buffer-based position (iOS or desktop after crossfade)
    if (this.soundBuffer && this.audioContext && this.iosStartTime > 0) {
      const elapsed = this.audioContext.currentTime - this.iosStartTime;
      this._bgPosition = this.loop
        ? elapsed % this.soundBuffer.duration
        : Math.min(elapsed, this.soundBuffer.duration);
      console.log(`[SoundscapeService] Stored background position: ${this._bgPosition.toFixed(2)}s`);
    } else if (!this.isIOS && this.audioElement) {
      this._bgPosition = this.audioElement.currentTime || 0;
      console.log(`[SoundscapeService] Stored background position (non-iOS): ${this._bgPosition.toFixed(2)}s`);
    }
  }

  /**
   * V1.1.1: Called on first user interaction after returning from background
   * when audioContext.resume() required a gesture.
   */
  async gestureResume() {
    if (!this._needsGestureResume) return false;
    this._needsGestureResume = false;
    console.log('[SoundscapeService] gestureResume() — user tapped after lock/unlock');
    return this.resumeIfNeeded();
  }
  
  // ========================================================================
  // GETTERS
  // ========================================================================
  
  /**
   * Setup MediaSession API for lock screen / background controls on iOS.
   * Shows the track name and provides play/pause/next/stop controls on the lock screen.
   */
  _setupMediaSession(track) {
    if (!('mediaSession' in navigator)) return;
    
    try {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: track?.name || track?.display_name || 'Paysage Sonore',
        artist: 'Hearen',
        album: track?.category || 'Soundscapes',
        artwork: [
          { src: '/logo192.png', sizes: '192x192', type: 'image/png' }
        ]
      });
      
      navigator.mediaSession.setActionHandler('play', () => {
        this.resume();
      });
      
      navigator.mediaSession.setActionHandler('pause', () => {
        this.pause();
      });
      
      navigator.mediaSession.setActionHandler('nexttrack', () => {
        if (this._mediaSessionHandlers.onNext) {
          this._mediaSessionHandlers.onNext();
        }
      });
      
      navigator.mediaSession.setActionHandler('previoustrack', () => {
        if (this._mediaSessionHandlers.onPrev) {
          this._mediaSessionHandlers.onPrev();
        }
      });
      
      try {
        navigator.mediaSession.setActionHandler('stop', async () => {
          if (this._mediaSessionHandlers.onStop) {
            this._mediaSessionHandlers.onStop();
          } else {
            await this.stopWithFade(150, { reason: 'mediaSession' });
          }
        });
      } catch (e) { /* stop not supported on all browsers */ }
      
      console.log('[SoundscapeService] MediaSession metadata set for lock screen');
    } catch (e) {
      console.warn('[SoundscapeService] MediaSession setup error:', e);
    }
  }

  /**
   * Register callbacks for MediaSession hardware controls (next/prev/stop).
   * Called by React components to wire their specific navigation logic.
   * @param {{ onNext?: Function, onPrev?: Function, onStop?: Function }} handlers
   * @returns {Function} cleanup function to unregister handlers
   */
  setMediaSessionHandlers(handlers = {}) {
    this._mediaSessionHandlers = {
      onNext: handlers.onNext || null,
      onPrev: handlers.onPrev || null,
      onStop: handlers.onStop || null
    };
    return () => {
      this._mediaSessionHandlers = { onNext: null, onPrev: null, onStop: null };
    };
  }

  getCurrentTrack() {
    return this.currentTrack;
  }
  
  getPlaybackState() {
    return this.playbackState;
  }
  
  getIsPlaying() {
    return this.isPlaying;
  }
  
  getIsPaused() {
    return this.isPaused;
  }
  
  // BUG A FIX: Expose analyser node for spectrum visualization
  getAnalyserNode() {
    return this.analyserNode;
  }
  
  getAudioContext() {
    return this.audioContext;
  }
  
  /**
   * BUILD 66.3: Get spectrum data for visualization
   * Now uses audioService's analysers to get both original and processed (with EQ) data
   */
  getSpectrumData() {
    // BUILD 66.3: Delegate to audioService since we share the same AudioContext
    // This gives us both original (green bars) and processed (orange bars with EQ)
    if (this.usesSharedContext) {
      return audioService.getSpectrumData();
    }
    
    // Fallback for non-shared context (shouldn't happen normally)
    if (!this.analyserNode || !this.audioContext) {
      return null;
    }
    
    if (this.audioContext.state !== 'running') {
      return null;
    }
    
    const bufferLength = this.analyserNode.frequencyBinCount;
    const processedData = new Uint8Array(bufferLength);
    
    this.analyserNode.getByteFrequencyData(processedData);
    
    return {
      original: processedData,
      processed: processedData,
      sampleRate: this.audioContext.sampleRate,
      bufferLength: bufferLength,
      boostedFrequencies: [],
      hasActiveFilters: false
    };
  }
  
  // V1.1.1: duck/unduck removed — ducking now handled via musicBusGain in audioService graph
}

// Export singleton
const soundscapeService = new SoundscapeService();
export default soundscapeService;
